import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslate } from '../i18n/react';
import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  /** Held until the dialog has what it asks for — a reason, say. */
  confirmDisabled?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

/**
 * Confirmation on the platform's own `<dialog>`.
 *
 * <p>`showModal()` brings the focus trap, Esc, the backdrop and returning focus to whatever
 * opened it — all of it, for free, in every browser we support. A hand-rolled overlay gets
 * two of those four right and the other two are the ones a keyboard user needs.</p>
 *
 * <p>The body should say what the delete <em>costs</em>: "this question is used in 13
 * templates" is the sentence that stops the click.</p>
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = 'danger',
  confirmDisabled = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const t = useTranslate();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  async function confirm() {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="nv-dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => {
        // The backdrop is the dialog element itself; anything inside stops here.
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="nv-modal__header">
        <h2 id={titleId} className="nv-h2">{title}</h2>
      </div>
      <div className="nv-modal__body nv-muted">{children}</div>
      <div className="nv-modal__footer">
        <Button onClick={onClose} disabled={busy}>{t('common.dismiss')}</Button>
        <Button variant={tone} onClick={confirm} disabled={confirmDisabled} loading={busy} loadingText={t('common.saving')}>
          {confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}
